'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'

export const CursorMiniIcon = ({ defaultIcon = '/icons/ms-icon-70x70.png', offsetX = 10, offsetY = 10 }) => {
  const canvasRef = useRef(null)
  const cursorRef = useRef({ x: 0, y: 0 })
  const animationFrameRef = useRef(null)
  const imageRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)

  // Función para cargar imágenes
  const loadImage = useCallback(icon => {
    const size = { width: 70, height: 70 }

    if (imageRef.current?.src === icon) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = icon
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = size.width
      canvas.height = size.height
      ctx.drawImage(img, 0, 0, size.width, size.height)

      const resizedImg = new Image()
      resizedImg.src = canvas.toDataURL()
      resizedImg.onload = () => {
        imageRef.current = resizedImg
        setIsLoading(false)
      }
    }

    img.onerror = error => {
      console.error(`Error al cargar la imagen: ${icon}`, error)
      setIsLoading(false)
    }
  }, [])

  // Función para dibujar texto de fallback
  const drawFallbackText = (context, x, y, floatOffsetY) => {
    context.font = '16px serif'
    context.textBaseline = 'middle'
    context.textAlign = 'center'
    context.fillText('🍍', x + offsetX + 10, y + offsetY + 10 + floatOffsetY)
  }

  // Función para aplicar transformaciones al contexto
  const applyTransformations = (context, x, y, floatOffsetY, rotationAngle) => {
    context.translate(x + offsetX + 16, y + offsetY + 16 + floatOffsetY)
    context.rotate(rotationAngle)
    context.translate(-(x + offsetX + 16), -(y + offsetY + 16 + floatOffsetY))
  }

  const onMouseMove = e => {
    cursorRef.current.x = e.clientX
    cursorRef.current.y = e.clientY
  }

  const onWindowResize = () => {
    const canvas = canvasRef.current
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
  }

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    const init = () => {
      canvas.style.position = 'fixed'
      canvas.style.pointerEvents = 'none'
      canvas.style.top = '0'
      canvas.style.left = '0'
      canvas.style.zIndex = '60'
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      document.body.appendChild(canvas)

      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('resize', onWindowResize)
      loop()
    }

    const draw = () => {
      if (!context) return
      context.clearRect(0, 0, canvas.width, canvas.height)
      const { x, y } = cursorRef.current

      const time = Date.now() * 0.002
      const floatOffsetY = Math.sin(time) * 5
      const rotationAngle = Math.sin(time) * 0.1

      if (isLoading || !imageRef.current) {
        drawFallbackText(context, x, y, floatOffsetY)
      } else {
        try {
          context.save()
          applyTransformations(context, x, y, floatOffsetY, rotationAngle)
          context.drawImage(imageRef.current, x + offsetX, y + offsetY + floatOffsetY, 32, 32)
          context.restore()
        } catch (error) {
          console.warn('Error al dibujar la imagen:', error)
          drawFallbackText(context, x, y, floatOffsetY)
        }
      }
    }

    const loop = () => {
      draw()
      animationFrameRef.current = requestAnimationFrame(loop)
    }

    init()
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('resize', onWindowResize)
      if (canvas) {
        canvas.remove()
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [offsetX, offsetY, isLoading])

  useEffect(() => {
    loadImage(defaultIcon)
  }, [defaultIcon, loadImage])

  return <canvas id='canvas-cursor-mini-icon' ref={canvasRef} />
}

CursorMiniIcon.displayName = 'CursorMiniIcon'
export default CursorMiniIcon
